import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { ITrackSubmitProps } from './types';
import { formContextObj } from '../../containers/FormContainer';
import { useDeviceType } from '@cian/ui-kit/responsive/hooks/useDeviceType';
import { selectAnonymousUserId } from '../../selectors/selectUser';
import {
  trackAddressFailEvent,
  trackBackToPageEvent,
  trackClickEvent,
  trackCompleteButtonEvent,
  trackEvent,
  trackFormSend,
  trackPrevStepEvent,
} from './tracking';

export const useFormTracking = () => {
  const deviceType = useDeviceType();
  const { currentQuestion, answersState } = useContext(formContextObj);

  const anonymousUserId = useSelector(selectAnonymousUserId);
  const [isDirty, setDirty] = useState(false);
  const trackChange = useCallback(() => {
    setDirty(true);
  }, []);

  const trackSubmit = useCallback(
    ({ field, isRequired, value }: ITrackSubmitProps) => {
      trackClickEvent({
        field,
        answersState,
        deviceType,
        isRequired,
        value,
        isDirty,
        anonymousUserId,
      });
    },
    [answersState, deviceType, isDirty, anonymousUserId],
  );

  const trackLteWithoutGte = useCallback(
    ({ field, isRequired }: ITrackSubmitProps) => {
      if (isDirty) {
        return;
      }
      trackClickEvent({
        field: `${field}_lte`,
        answersState,
        deviceType,
        isRequired,
        value: null,
        isDirty: true,
        anonymousUserId,
      });
    },
    [isDirty, answersState, deviceType, anonymousUserId],
  );

  const trackBackToPage = useCallback(() => {
    trackBackToPageEvent({ field: currentQuestion.answerField, answersState, deviceType, anonymousUserId });
  }, [currentQuestion, answersState, deviceType, anonymousUserId]);

  const trackPrevStep = useCallback(
    (currentStepField: string) => {
      trackPrevStepEvent({
        field: currentStepField,
        answersState,
        deviceType,
        anonymousUserId,
      });
    },
    [anonymousUserId, answersState, deviceType],
  );

  const trackAddressFail = useCallback(() => {
    trackAddressFailEvent({ field: currentQuestion.answerField, answersState, deviceType, anonymousUserId });
  }, [currentQuestion, answersState, deviceType, anonymousUserId]);

  const trackCompleteButton = useCallback(() => {
    trackCompleteButtonEvent({ field: currentQuestion.answerField, answersState, deviceType, anonymousUserId });
  }, [currentQuestion, answersState, deviceType, anonymousUserId]);

  const trackFormSendEvent = useCallback(() => {
    trackFormSend({ field: currentQuestion.answerField, answersState, deviceType, anonymousUserId });
  }, [currentQuestion, answersState, deviceType, anonymousUserId]);

  const trackWantToChooseRealtorEvent = useCallback(
    (value: string) => {
      trackEvent({
        field: currentQuestion.answerField,
        answersState,
        deviceType,
        action: 'Click_application_type',
        label: `isRequired/default/${value}`,
        anonymousUserId,
      });
    },
    [currentQuestion, answersState, deviceType, anonymousUserId],
  );

  return {
    trackChange,
    trackSubmit,
    trackBackToPage,
    trackPrevStep,
    trackAddressFail,
    trackCompleteButton,
    trackLteWithoutGte,
    trackFormSendEvent,
    trackWantToChooseRealtorEvent,
  };
};
